/**
 * 자동 리깅 — 정면 실루엣에서 휴머노이드 뼈대를 세운다.
 *
 * 리깅 도구 없이 게임에 넣을 수 있으려면 뼈와 스킨 가중치와 클립이 glb 안에 있어야
 * 한다. 여기서는 A포즈 2족 캐릭터를 가정하고, 정면 실루엣의 **행별 점유 구간(run)**
 * 으로 관절을 찾는다:
 *   - 허벅지 높이(t≈0.2)에서 구간이 둘이면 그 중심이 두 다리.
 *   - 가슴 높이(t≈0.65)에서 구간이 셋이면 바깥 둘이 두 팔, 가운데가 몸통.
 *   - 팔이 셋으로 갈라져 있는 가장 낮은 행이 손끝.
 * 못 찾으면 비율 기본값으로 물러선다 — 골렘처럼 팔이 몸에 붙은 캐릭터도 뼈는 선다.
 *
 * 스킨 가중치는 **캡슐 부호 거리**로 준다. 처음 판은 뼈 선분까지의 거리를 그대로
 * 가우시안에 넣었는데, 몸통 뼈의 반지름이 팔 뼈의 두 배가 넘어 팔 표면 정점이 팔·몸통
 * 반반으로 묶였다 — 팔을 들면 표면이 절반만 따라가며 고무처럼 늘어났다(2026-09-04 실측).
 * 지금은 정점이 어느 캡슐 **안에** 있으면 그 뼈가 전부 가져가고, 캡슐 밖으로 나간 거리만
 * 좁은 띠(blend)로 섞는다. 캡슐 반지름은 실루엣 구간 폭에서 읽는다.
 */

import * as THREE from 'three';

export interface SilhouetteSampler {
  /** s: 0..1 가로, t: 0..1 세로(0=바닥). 0.5 이상이 안쪽. */
  sample(s: number, t: number): number;
}

export interface JointLayout {
  hips: THREE.Vector3;
  chest: THREE.Vector3;
  neck: THREE.Vector3;
  head: THREE.Vector3;
  shoulderL: THREE.Vector3; elbowL: THREE.Vector3; handL: THREE.Vector3;
  shoulderR: THREE.Vector3; elbowR: THREE.Vector3; handR: THREE.Vector3;
  thighL: THREE.Vector3; kneeL: THREE.Vector3; footL: THREE.Vector3;
  thighR: THREE.Vector3; kneeR: THREE.Vector3; footR: THREE.Vector3;
  /** 다리·팔을 실루엣에서 실제로 찾았는가 */
  humanoid: boolean;
  /** 팔만이라도 찾았는가 (T-포즈는 이게 있어야 뜻이 있다) */
  armsFound: boolean;
  /** 실루엣에서 읽은 반지름(지오메트리 단위): 팔·다리·몸통·머리 */
  radii: { arm: number; leg: number; torso: number; head: number };
}

export interface RigResult {
  skinned: THREE.SkinnedMesh;
  skeleton: THREE.Skeleton;
  bones: Record<BoneName, THREE.Bone>;
  layout: JointLayout;
  height: number;
}

export type BoneName =
  | 'hips' | 'spine' | 'chest' | 'neck' | 'head'
  | 'upperArmL' | 'forearmL' | 'handL'
  | 'upperArmR' | 'forearmR' | 'handR'
  | 'thighL' | 'shinL' | 'footL'
  | 'thighR' | 'shinR' | 'footR';

/** 한 행의 점유 구간들 [s0, s1]. */
function runsAt(sil: SilhouetteSampler, t: number, samples = 96): [number, number][] {
  const runs: [number, number][] = [];
  let start = -1;
  for (let i = 0; i <= samples; i++) {
    const s = i / samples;
    const on = i < samples && sil.sample(s, t) >= 0.5;
    if (on && start < 0) start = s;
    if (!on && start >= 0) {
      if (s - start > 0.03) runs.push([start, s]);
      start = -1;
    }
  }
  return runs;
}

const mid = (r: [number, number]) => (r[0] + r[1]) / 2;

/**
 * 관절 배치. 좌표는 지오메트리 공간 — box 안에 s,t 를 선형으로 펼친다.
 * 캐릭터의 왼쪽(L)은 화면 오른쪽(+x)이다 (정면을 보고 있으니까).
 */
export function layoutJoints(sil: SilhouetteSampler, box: THREE.Box3): JointLayout {
  const size = new THREE.Vector3();
  box.getSize(size);
  const P = (s: number, t: number) => new THREE.Vector3(box.min.x + s * size.x, box.min.y + t * size.y, 0);

  // 다리: 0.12~0.32 구간에서 두 구간이 가장 안정적으로 나오는 행을 찾는다.
  let legs: [number, number] | null = null;
  for (const t of [0.22, 0.18, 0.26, 0.14, 0.3]) {
    const r = runsAt(sil, t);
    if (r.length === 2) { legs = [mid(r[0]!), mid(r[1]!)]; break; }
  }
  // 팔: 0.55~0.75 에서 세 구간. 손끝은 세 구간이 유지되는 가장 낮은 행.
  let arms: [number, number] | null = null;
  let handT = 0.45;
  for (const t of [0.66, 0.62, 0.7, 0.58, 0.74]) {
    const r = runsAt(sil, t);
    if (r.length === 3) { arms = [mid(r[0]!), mid(r[2]!)]; break; }
  }
  if (arms) {
    for (let t = 0.66; t >= 0.3; t -= 0.02) {
      if (runsAt(sil, t).length >= 3) handT = t; else break;
    }
  }
  const humanoid = Boolean(legs && arms);

  // 반지름: 구간 폭의 절반. 못 찾으면 키 비율 기본값.
  const widthOf = (rs: [number, number][], pick: (r: [number, number][]) => [number, number] | undefined) => {
    const r = pick(rs); return r ? (r[1] - r[0]) * size.x * 0.5 : 0;
  };
  const legRuns = runsAt(sil, 0.22);
  const legR0 = legRuns.length === 2 ? (widthOf(legRuns, (r) => r[0]) + widthOf(legRuns, (r) => r[1])) / 2 : 0.09 * size.y;
  const armRows = [0.66, 0.62, 0.7].map((t) => runsAt(sil, t)).filter((r) => r.length === 3);
  const armR0 = armRows.length ? armRows.reduce((acc, r) => acc + (widthOf(r, (x) => x[0]) + widthOf(r, (x) => x[2])) / 2, 0) / armRows.length : 0.07 * size.y;
  const headRuns = runsAt(sil, 0.93);
  const headR0 = headRuns.length ? widthOf(headRuns, (r) => r.reduce((a, b) => (b[1] - b[0] > a[1] - a[0] ? b : a))) : 0.1 * size.y;

  const legL = legs ? Math.max(legs[0]!, legs[1]!) : 0.62;
  const legR = legs ? Math.min(legs[0]!, legs[1]!) : 0.38;
  const armL = arms ? Math.max(arms[0]!, arms[1]!) : 0.82;
  const armR = arms ? Math.min(arms[0]!, arms[1]!) : 0.18;
  const shoulderT = 0.8;
  const torsoRuns = runsAt(sil, shoulderT);
  const torsoW = torsoRuns.length ? (torsoRuns.length === 3 ? torsoRuns[1]![1] - torsoRuns[1]![0] : torsoRuns[0]![1] - torsoRuns[0]![0]) : 0.4;
  const shoulderHalf = Math.min(0.45, Math.max(0.12, torsoW * 0.5));
  const radii = {
    arm: Math.max(0.03 * size.y, armR0),
    leg: Math.max(0.04 * size.y, legR0),
    torso: Math.max(0.08 * size.y, torsoW * size.x * 0.5),
    head: Math.max(0.05 * size.y, headR0),
  };

  return {
    radii,
    armsFound: Boolean(arms),
    hips: P(0.5, 0.5),
    chest: P(0.5, 0.7),
    neck: P(0.5, 0.84),
    head: P(0.5, 0.94),
    shoulderL: P(0.5 + shoulderHalf, shoulderT),
    elbowL: P((0.5 + shoulderHalf + armL) / 2, (shoulderT + handT) / 2 + 0.02),
    handL: P(armL, handT),
    shoulderR: P(0.5 - shoulderHalf, shoulderT),
    elbowR: P((0.5 - shoulderHalf + armR) / 2, (shoulderT + handT) / 2 + 0.02),
    handR: P(armR, handT),
    thighL: P(legL, 0.5),
    kneeL: P(legL, 0.27),
    footL: P(legL, 0.03),
    thighR: P(legR, 0.5),
    kneeR: P(legR, 0.27),
    footR: P(legR, 0.03),
    humanoid,
  };
}

interface Segment { name: BoneName; a: THREE.Vector3; b: THREE.Vector3; radius: number }

/** 뼈 하나 = 부모 관절에서 자식 관절까지의 선분. */
function segments(j: JointLayout, height: number): Segment[] {
  const r = height;
  const { arm, leg, torso, head } = j.radii;
  return [
    { name: 'hips', a: j.hips, b: j.chest.clone().lerp(j.hips, 0.5), radius: torso },
    { name: 'spine', a: j.hips.clone().lerp(j.chest, 0.5), b: j.chest, radius: torso },
    { name: 'chest', a: j.chest, b: j.neck, radius: torso * 1.05 },
    { name: 'neck', a: j.neck, b: j.head, radius: head * 0.6 },
    { name: 'head', a: j.head, b: j.head.clone().add(new THREE.Vector3(0, 0.08 * r, 0)), radius: head },
    { name: 'upperArmL', a: j.shoulderL, b: j.elbowL, radius: arm },
    { name: 'forearmL', a: j.elbowL, b: j.handL, radius: arm * 0.9 },
    { name: 'handL', a: j.handL, b: j.handL.clone().add(new THREE.Vector3(0, -0.05 * r, 0)), radius: arm },
    { name: 'upperArmR', a: j.shoulderR, b: j.elbowR, radius: arm },
    { name: 'forearmR', a: j.elbowR, b: j.handR, radius: arm * 0.9 },
    { name: 'handR', a: j.handR, b: j.handR.clone().add(new THREE.Vector3(0, -0.05 * r, 0)), radius: arm },
    { name: 'thighL', a: j.thighL, b: j.kneeL, radius: leg },
    { name: 'shinL', a: j.kneeL, b: j.footL, radius: leg * 0.85 },
    { name: 'footL', a: j.footL, b: j.footL.clone().add(new THREE.Vector3(0, 0, 0.08 * r)), radius: leg },
    { name: 'thighR', a: j.thighR, b: j.kneeR, radius: leg },
    { name: 'shinR', a: j.kneeR, b: j.footR, radius: leg * 0.85 },
    { name: 'footR', a: j.footR, b: j.footR.clone().add(new THREE.Vector3(0, 0, 0.08 * r)), radius: leg },
  ];
}

const PARENT: Record<BoneName, BoneName | null> = {
  hips: null, spine: 'hips', chest: 'spine', neck: 'chest', head: 'neck',
  upperArmL: 'chest', forearmL: 'upperArmL', handL: 'forearmL',
  upperArmR: 'chest', forearmR: 'upperArmR', handR: 'forearmR',
  thighL: 'hips', shinL: 'thighL', footL: 'shinL',
  thighR: 'hips', shinR: 'thighR', footR: 'shinR',
};

function pointSegmentDistance(p: THREE.Vector3, a: THREE.Vector3, b: THREE.Vector3): number {
  const ab = b.clone().sub(a);
  const len2 = ab.lengthSq();
  const t = len2 > 0 ? THREE.MathUtils.clamp(p.clone().sub(a).dot(ab) / len2, 0, 1) : 0;
  return p.distanceTo(a.clone().addScaledVector(ab, t));
}

/**
 * 지오메트리에 뼈를 심는다. 지오메트리는 인덱스가 있어야 하고 position 을 가져야 한다.
 * skinIndex/skinWeight 를 붙이고 SkinnedMesh 를 돌려준다. 원래 머티리얼을 그대로 쓴다.
 */
export function rigGeometry(
  geometry: THREE.BufferGeometry,
  material: THREE.Material,
  sil: SilhouetteSampler,
): RigResult {
  geometry.computeBoundingBox();
  const box = geometry.boundingBox!;
  const size = new THREE.Vector3();
  box.getSize(size);
  const height = size.y;
  const layout = layoutJoints(sil, box);
  const segs = segments(layout, height);
  const order = segs.map((s) => s.name);

  // 뼈 계층. 각 뼈의 위치는 부모 기준 로컬이다.
  const bones = {} as Record<BoneName, THREE.Bone>;
  for (const seg of segs) {
    const bone = new THREE.Bone();
    bone.name = seg.name;
    bones[seg.name] = bone;
  }
  for (const seg of segs) {
    const parentName = PARENT[seg.name];
    const bone = bones[seg.name];
    if (parentName) {
      const parentSeg = segs.find((s) => s.name === parentName)!;
      bones[parentName].add(bone);
      bone.position.copy(seg.a).sub(parentSeg.a);
    } else {
      bone.position.copy(seg.a);
    }
  }

  // 스킨 가중치: 캡슐 부호 거리. 캡슐 안 = 1, 밖은 blend 폭의 가우시안으로 급히 0.
  // 캡슐끼리 겹치는 관절 부근에서만 둘 이상이 섞인다 — 그게 관절이다.
  const pos = geometry.getAttribute('position');
  const skinIndex = new Uint16Array(pos.count * 4);
  const skinWeight = new Float32Array(pos.count * 4);
  const p = new THREE.Vector3();
  const scored: { i: number; w: number }[] = [];
  const blend = height * 0.045;
  for (let v = 0; v < pos.count; v++) {
    p.fromBufferAttribute(pos, v);
    scored.length = 0;
    for (let i = 0; i < segs.length; i++) {
      const seg = segs[i]!;
      const d = pointSegmentDistance(p, seg.a, seg.b);
      const outside = Math.max(0, d - seg.radius) / blend;
      const w = Math.exp(-outside * outside * 2);
      if (w > 1e-3) scored.push({ i, w });
    }
    scored.sort((a, b) => b.w - a.w);
    const top = scored.slice(0, 4);
    let sum = top.reduce((acc, s) => acc + s.w, 0);
    if (top.length === 0 || sum < 1e-6) {
      // 어느 뼈에도 안 가까우면 가장 가까운 뼈 하나에 통째로.
      let best = 0, bestD = Infinity;
      for (let i = 0; i < segs.length; i++) {
        const d = pointSegmentDistance(p, segs[i]!.a, segs[i]!.b);
        if (d < bestD) { bestD = d; best = i; }
      }
      top.length = 0; top.push({ i: best, w: 1 }); sum = 1;
    }
    for (let k = 0; k < 4; k++) {
      skinIndex[v * 4 + k] = top[k]?.i ?? 0;
      skinWeight[v * 4 + k] = top[k] ? top[k]!.w / sum : 0;
    }
  }
  geometry.setAttribute('skinIndex', new THREE.BufferAttribute(skinIndex, 4));
  geometry.setAttribute('skinWeight', new THREE.BufferAttribute(skinWeight, 4));

  const skinned = new THREE.SkinnedMesh(geometry, material);
  skinned.name = 'TexelForge';
  const root = bones.hips;
  skinned.add(root);
  const skeleton = new THREE.Skeleton(order.map((n) => bones[n]));
  skinned.bind(skeleton);
  skinned.frustumCulled = false;

  return { skinned, skeleton, bones, layout, height };
}
